import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, ExternalLink, Sparkles, Skull } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FactCard({ f }) {
  return (
    <div className="es-fact" data-testid="monster-fact">
      {f.sectionTitle && <div className="es-fact-head">{f.sectionTitle}</div>}
      <div className="es-fact-rows">
        {Object.entries(f.row).map(([k, v]) => (
          <div key={k} className="es-fact-row">
            <span className="es-fact-k">{k || "—"}</span>
            <span className="es-fact-v">{v}</span>
          </div>
        ))}
      </div>
      {f.sourceUrl && (
        <a className="es-inline-link" href={f.sourceUrl} target="_blank" rel="noreferrer">
          <span className="es-badge">{f.language}</span> <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

export default function MonsterDetail() {
  const { slug } = useParams();
  const { lang, t, dir } = useLang();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(false);
    axios.get(`${API}/monsters/${slug}?lang=${lang}`)
      .then(({ data }) => { if (active) setD(data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug, lang]);

  if (loading) return <div className="es-page es-state" data-testid="monster-detail-loading"><Loader2 className="es-spin" size={22} /> {t("common.loading")}</div>;
  if (error || !d) return <div className="es-page es-state" data-testid="monster-detail-error">{t("common.noResults")} <Link className="es-inline-link" to="/monsters">{t("common.back")}</Link></div>;

  const name = d.localizedName || d.name;
  const askAi = () => navigate(`/?q=${encodeURIComponent(`Tell me about the monster ${d.name} in Evony`)}`);
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Thing", name: d.name,
    description: `${d.name} is a Monster/Boss in Evony: The King's Return.`,
  };

  return (
    <div className="es-page es-detail" data-testid="page-monster-detail" dir={dir}>
      <Seo title={`${d.name} — Evony Monster & Boss Guide | Evony Strategist`}
        description={`${d.name} — power, level and guide references for this Evony monster/boss, sourced from the Evony Guide Wiki.`}
        path={`/monsters/${d.slug}`} jsonLd={jsonLd} />

      <Link to="/monsters" className="es-back" data-testid="monster-back"><ArrowLeft size={16} /> {t("monsters.title")}</Link>

      <div className="es-detail-head">
        <div className="es-detail-icon"><Skull size={26} /></div>
        <div>
          <h1 className="es-h1" data-testid="monster-name">{name}</h1>
          {d.localizedName && d.localizedName !== d.name && <p className="es-detail-alt">{d.name}</p>}
          <div className="es-badges">
            {d.power != null && <span className="es-badge es-badge-gold">{t("monsters.power")}: {d.power}</span>}
            {d.level != null && <span className="es-badge">{t("monsters.level")}: {d.level}</span>}
            {d.monsterType && <span className="es-badge">{d.monsterType}</span>}
          </div>
        </div>
      </div>

      <button className="es-btn-primary es-askai" data-testid="monster-askai" onClick={askAi}>
        <Sparkles size={16} /> {t("common.askAi")}
      </button>

      {d.articles?.length > 0 && (
        <section className="es-detail-section" data-testid="monster-appears">
          <h2 className="es-h2">{t("common.appearsIn")}</h2>
          <div className="es-link-list">
            {d.articles.map((a) => (
              <Link key={a.slug} to={`/guides/${a.slug}`} className="es-related-link">
                {a.title} {a.category && <span className="es-badge">{a.category}</span>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {d.facts?.length > 0 && (
        <section className="es-detail-section" data-testid="monster-facts">
          <h2 className="es-h2">{t("common.facts")}</h2>
          <div className="es-fact-grid">{d.facts.map((f, i) => <FactCard key={i} f={f} />)}</div>
        </section>
      )}
    </div>
  );
}
